import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, FormControlLabel, InputAdornment, Switch, TextField, Typography } from '@mui/material';
import OrganisationDropdown from '../../Shared/OrganisationDropdown/index.tsx';
import BotNumberDropdown from '../../Shared/BotNumberDropdown/index.tsx';
import { Edge, FormData, Node, TransformedNode, ValidationErrors } from '../../../interfaces/Flowbuilder.interface.ts';
import { FlowBuilderActions } from '../../../Services/_actions/index.js';
import { useSnackBar } from '../../FlowBuilder/Common/SnackBar/index.tsx';
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';
import { flowValidationAtom } from '../../../Services/_states/index.js';
import LoadingButton from '../../Shared/Loading/index.tsx';
import { setUpFlowDataAtom, setUpFlowNodeEdgeAtom } from '../../../Services/_states/Ecommerce/index.ts';

const UpdateForm: React.FC = () => {
    const actions = FlowBuilderActions();
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [flowObj, setFlowObj] = useRecoilState<any>(setUpFlowNodeEdgeAtom);
    const [flowId, setFlowId] = useState("");
    const { showSnackBar } = useSnackBar();
    const [formData, setFormData] = useRecoilState(setUpFlowDataAtom);
    useEffect(() => {
        if (formData?._id) {
            setFlowId(formData?._id);
        }
    }, [])
    const [validationErrors, setValidationErrors] = useState<ValidationErrors>({
        organisation: '',
        phoneNumber: '',
        patterns: '',
        title: '',
        // alias: '',
    });
    const flowValidationObj = useSetRecoilState(flowValidationAtom);

    const validateForm = async () => {
        const errors: any = {};

        if (!formData?.title?.trim()) {
            errors.title = 'Flow name is required.';
        }

        if (!formData.patterns.length || formData.patterns[0] === '') {
            errors.patterns = 'At least one keyword is required.';
        }

        if (!formData?.selectedOrganisation) {
            errors.organisation = 'Organization is required.';
        }

        if (!formData?.phone_number_id) {
            errors.phoneNumber = 'Phone Number is required.';
        }

        if (!flowId) {  // Only check for duplicate flow name when creating a new flow
            if (formData?.title && formData?.company_id && formData?.phone_number_id) {
                try {
                    const res: any = await actions.checkFlowName(formData);
                    if (res?.length > 0) {
                        errors.title = `Flow name "${formData?.title?.trim()}" already exists.`;
                    }
                } catch (error) {
                    errors.title = 'Error validating flow name. Please try again.';
                }
            }
        }
        if (formData?.company_id && formData?.phone_number_id && formData?.patterns?.length) {
            try {
                if (formData?.patterns?.length > 0) {
                    const response: any = await actions.checkPatterns(formData, formData?.patterns);
                    if (response?.data?.length > 0) {
                        errors.patterns = `Duplicate keywords found: ${response?.data?.join(', ')}`;
                    }
                }
            } catch (error) {
                errors.patterns = 'Failed to validate keywords. Please try again.';
            }
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const validateNodes = (nodes: Node[]): Record<string, any> => {
        const errors: Record<string, any> = {};
        nodes.forEach((node) => {
            const { type, data, id } = node;
            const nodeErrors: Record<string, any> = {};
            if (!data?.body)
                nodeErrors.body = 'Body is missing';
            if (type === "orderDetails" && !data?.configuration_name)
                nodeErrors.configuration_name = 'Configuration name is missing';
            if (['image', 'video', 'audio', 'document'].includes(type) && !data?.media?.s3_object?.url) {
                nodeErrors.url = `${type.charAt(0).toUpperCase() + type.slice(1)} URL is missing`;
            }
            if (Object.keys(nodeErrors).length) {
                errors[id] = nodeErrors;
            }
        });

        return errors;
    };


    const convertNodesAndEdges = (nodes: Node[], edges: Edge[], flowId?: string): { rules: TransformedNode[] } => {
        const rules: TransformedNode[] = nodes.map((node) => {
            const { id, type, data: nodeData } = node;
            const inputContext = edges.filter((edge) => edge.target === id).map((edge) => edge.source);
            const outputContext = edges
                ?.filter((edge) => edge?.source === id)
                ?.map((edge) => ({
                    intent_id: edge?.target,
                    flow_id: flowId,
                }));
            const transformedNode: TransformedNode = {
                intent_id: id,
                input_context: inputContext?.length ? inputContext : [],
                output_context: outputContext?.length ? outputContext : [],
                is_first: inputContext?.length === 0,
                message: {
                    type: type.toUpperCase(),
                    header: { type: 'TEXT', text: nodeData?.header || '' },
                    body: { type: 'TEXT', text: nodeData?.body || '', parameters: nodeData?.params || {} },
                    footer: { type: 'TEXT', text: nodeData?.footer || '' },
                    configuration_name: nodeData?.configuration_name || '',
                },
            };
            transformedNode.trigger = {};
            transformedNode.result = nodeData?.triggerSettings?.result || {};
            if (type !== "orderStatus") {
                transformedNode.result = {
                    type: "JSON",
                    label: `result__${type}`,
                    value: `result__${type}`,
                    paramType: "result",
                    output_result: "input-value",
                    custom_value: ""
                }
            }
            if (['image', 'video', 'audio', 'document'].includes(type)) {
                transformedNode.message.media = { ...nodeData?.media };
            }
            return transformedNode;
        });

        return { rules };
    };

    const handleSaveFlow = async (flowId?: string) => {
        if (!flowObj) return;
        try {
            await actions.createNodeEdges({ nodes: flowObj?.nodes || [], edges: flowObj?.edges || [], flow_id: flowId });

        } catch (error) {
            showSnackBar('Failed to save flow. Please try again.', 'error');
        } finally {
            setIsSaving(false);
        }
    };


    const handleCreateOrUpdateFlow = async () => {
        setIsSaving(true);
        try {
            const isValid = await validateForm();
            const validationErrors = validateNodes(flowObj?.nodes);
            const nodeIsValid = Object.keys(validationErrors).length === 0;
            if (!nodeIsValid) {
                flowValidationObj(validationErrors);
                return;
            }
            if (!isValid) return;
            const {
                selectedCampaign,
                selectedForm,
                selectedOrganisation,
                selectedPhoneNumber,
                selectedTemplate,
                ...updateObj
            }: any = formData;
            if (!flowId) {
                // if (updateObj?._id) {
                //     updateObj.flow_id = updateObj?._id;
                //     delete updateObj._id;
                // }
                const result = convertNodesAndEdges(flowObj.nodes, flowObj.edges, flowId);
                let res: any = await actions.createFlow({ ...updateObj, ...result });
                if (res?.status === 201) {
                    setFlowId(res?.data?.id || '');
                    handleSaveFlow(res?.data?.id)
                    showSnackBar(`Flow "Created Successfully`, 'success');
                } else {
                    showSnackBar(`Flow Creation failed`, 'error');
                }
            } else {
                const result = convertNodesAndEdges(flowObj.nodes, flowObj.edges, flowId);
                handleSaveFlow(flowId)
                let res: any = await actions.updateFlow(flowId, { ...updateObj, ...result });
                if (res?.status === 200) {
                    showSnackBar('Flow Updated Successfully', 'success');
                } else {
                    showSnackBar('Flow Not Updated Successfully', 'error');
                }
            }
        } catch (error) {
            console.error('Error:', error?.message);
        } finally {
            setIsSaving(false);
        }
    };

    const fetchNodeEdgeById = async (flowId) => {
        try {
            let res = await actions.getNodeEdgesById(flowId);
            setFlowObj({ ...res });
        } catch (error) {
            console.warn(error);
        } finally {
        }
    };


    const fetchFlowDetailByFlowType = async (data) => {
        setIsLoading(true);
        try {
            setValidationErrors({
                organisation: '',
                phoneNumber: '',
                patterns: '',
                title: '',
            });
            flowValidationObj({});
            if (data?.company_id && data?.phone_number_id) {
                const res = await actions.fetchFlowDetailByFlowType(data);
                if (res?._id) {
                    fetchNodeEdgeById(res?._id);
                    setFlowId(res?._id);
                    setFormData((prevFormData) => ({
                        ...prevFormData,
                        ...res,
                    }));
                } else {
                    setFormData((prevFormData) => ({
                        ...prevFormData,
                        ...data,
                    }));
                }
            }
        } catch (error) {
            console.warn("Error fetching flow details:", error);
            setFormData((prevFormData) => ({
                ...prevFormData,
                ...data,
            }));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Box sx={{
            padding: "20px",
            maxWidth: "900px",
            margin: "auto",
            backgroundColor: "#f9f9f9",
            borderRadius: "8px",
            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",

        }}   >
            <Typography variant="h6" fontWeight="bold" gutterBottom>
                Setup Ecommerce Flow
            </Typography>
            <OrganisationDropdown
                label={
                    <Typography component="span">
                        Organization <span style={{ color: 'red' }}>*</span>
                    </Typography>
                }
                value={formData?.selectedOrganisation || null}
                onChange={(value) => {
                    setFormData({ ...formData, selectedOrganisation: value, company_id: value?.organisation_id || '', selectedPhoneNumber: null, phone_number_id: "", title: "" });
                    setValidationErrors({ ...validationErrors, organisation: '' }); // Clear error for organization
                }}
                error={!!validationErrors?.organisation}
                errorMessage={validationErrors?.organisation}
                sx={{ mb: 1, mt: 2 }}
            />
            {formData?.company_id && (
                <BotNumberDropdown
                    organisation={formData?.selectedOrganisation || null}
                    onChange={(value) => {
                        setValidationErrors({ ...validationErrors, phoneNumber: '' });

                        // Updating selected phone number before fetching details
                        const updatedData = {
                            ...formData,
                            selectedPhoneNumber: value,
                            phone_number_id: value?.wa_phone_number,
                            app_name: value?.app_name,
                            waba_id: value?.waba_id || '',
                        };
                        setFormData(updatedData); // Update form data first
                        fetchFlowDetailByFlowType(updatedData); // Then fetch flow details
                    }}
                    value={formData?.selectedPhoneNumber}
                    label={
                        <Typography component="span">
                            Phone Number <span style={{ color: 'red' }}>*</span>
                        </Typography>
                    }
                    error={!!validationErrors?.phoneNumber}
                    errorMessage={validationErrors?.phoneNumber}
                    sx={{ mb: 1 }}
                />
            )}

            <TextField
                label={
                    <Typography component="span">
                        Flow Name <span style={{ color: 'red' }}>*</span>
                    </Typography>
                }
                fullWidth
                value={formData?.title || ''}
                onChange={(e) => {
                    setFormData({ ...formData, title: e.target.value });
                    setValidationErrors({ ...validationErrors, title: '' }); // Clear error
                }}
                sx={{ marginBottom: 2 }}
                margin="dense"
                variant="outlined"
                size="small"
                error={!!validationErrors?.title}
                helperText={validationErrors?.title}
                InputProps={{
                    endAdornment: isLoading ? (
                        <InputAdornment position="end">
                            <CircularProgress size={20} />
                        </InputAdornment>
                    ) : null
                }}

            />
            {formData?.title && formData?.phone_number_id &&
                <FormControlLabel
                    control={
                        <Switch
                            checked={!!formData?.is_published}
                            onChange={() => setFormData({ ...formData, is_published: !formData.is_published })}
                        />
                    }
                    label="Publish Flow"
                    labelPlacement="start"
                />
            }
            <Box>
                <LoadingButton
                    buttonName="Save"
                    onClickButton={handleCreateOrUpdateFlow}
                    isDisabled={isSaving}
                    isLoading={isSaving}
                />

            </Box>
        </Box>
    );
};

export default UpdateForm;
